// src/Phaser/engine/PhaserScene.ts
import Phaser from "phaser";

import { EventManager } from "./events/EventManager";
import { WinConditionManager } from "./events/WinConditionManager";

import { Character } from "./entities/Character";
import { GameObject } from "./entities/Object";

import { DEFAULT_GRAVITY_Y } from "../../../constant/constant";

import type {
  PhaserConfig,
  SceneConfig,
  CharacterConfig,
  ObjectConfig,
  TextObjectConfig,
  WinCondition,
} from "../types/config";
import type { TKeyValueDataString } from "../../../models/objects/gen";

// NEW minisystems
import { AssetManager } from "./core/AssetManager";
import { ColliderRegistry } from "./core/ColliderRegistry";
import { AudioManager } from "./audio/AudioManager";
import { EntityManager } from "./entities/EntityManager";
import { CollisionSystem, PUSHABLE } from "./collision/CollisionSystem";

const normKey = (s?: string) => String(s ?? "").trim().replace(/\s+/g, "_");

export interface PhaserSceneConfig {
  PhaserConfig: PhaserConfig;
  eventManager?: EventManager | null;
  assets: TKeyValueDataString[];
}

export class PhaserScene extends Phaser.Scene {
  private adminMode: boolean;
  private configData: PhaserSceneConfig;
  private eventManager?: EventManager | null;
  private onWin: () => void;

  // mini-systems
  private assets!: AssetManager;
  private audio!: AudioManager;
  private colliders!: ColliderRegistry;
  private entities!: EntityManager;
  private collisions!: CollisionSystem;

  // Win conditions
  private winConditions: WinCondition[] = [];
  private winConditionManager?: WinConditionManager;

  // Pick order
  private nextPickOrder = 1;

  private domBgApplied = false;
  // Grid overlay (debug)
  private gridGraphics?: Phaser.GameObjects.Graphics;
  private gridEnabled = false;
  private gridCellSize = 50;

  private _hasWon = false;
  private winEventDisposer?: () => void;

  constructor(adminMode: boolean, configData: PhaserSceneConfig, onWin: () => void) {
    super({ key: "MainScene" });
    this.configData = configData;
    this.eventManager = configData.eventManager ?? null;
    this.winConditions = configData.PhaserConfig?.winConditions ?? [];
    this.onWin = onWin;
    this.adminMode = adminMode;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────────────────────────────────
  // ---- Dans PhaserScene.ts ----
  private lookupAssetUrl = (key: string): string | undefined => {
    const k = normKey(key);
    if (!k) return undefined;

    // 1) match exact
    const hit = this.configData.assets.find(a => normKey(a.key) === k);
    if (hit?.value) return hit.value;

    // 2) si clé numérique → essayer sprite_/obstacle_/scene_
    if (/^\d+$/.test(k)) {
      const prefs = [`sprite_${k}`, `obstacle_${k}`, `scene_${k}`];
      for (const p of prefs) {
        const h = this.configData.assets.find(a => normKey(a.key) === normKey(p));
        if (h?.value) return h.value;
      }
    }

    // 3) sinon → tenter les variantes de préfixes usuels autour d'un idPhaser
    // (évite les ratés "chargé sous char_<id> mais on cherche <id>")
    const altPrefs = [
      `char_${k}`, `obj_${k}`, `sprite_${k}`, `obstacle_${k}`, `scene_${k}`,
    ];
    for (const p of altPrefs) {
      const h = this.configData.assets.find(a => normKey(a.key) === normKey(p));
      if (h?.value) return h.value;
    }

    // 4) aucun match
    return undefined;
  };


  // Fallback DOM BG (évite le CORS car c’est un background CSS)
  private applyDomBackground(url: string, w: number, h: number) {
    const parent = (this.game?.canvas?.parentElement ?? null) as HTMLElement | null;
    if (!parent || !url) return;
    parent.style.backgroundImage = `url("${url}")`;
    parent.style.backgroundSize = "cover";
    parent.style.backgroundRepeat = "no-repeat";
    parent.style.backgroundPosition = "center";
    // Le ratio et la largeur sont portes par le conteneur React : forcer w/h en
    // pixels ici faisait deborder la scene des petits ecrans.
    parent.style.width = "100%";
    this.domBgApplied = true;
  }

  // Nettoyage du fallback DOM BG
  private clearDomBackground() {
    if (!this.domBgApplied) return;
    const parent = (this.game?.canvas?.parentElement ?? null) as HTMLElement | null;
    if (!parent) return;
    parent.style.backgroundImage = "";
    parent.style.backgroundSize = "";
    parent.style.backgroundRepeat = "";
    parent.style.backgroundPosition = "";
    // width/height restent pilotés par React
    this.domBgApplied = false;
  }


  /** Duplique toute texture à clé purement numérique sous un alias `scene_<n>` */
  private ensureSceneAliases() {
    const keys = this.textures.getTextureKeys();
    for (const k of keys) {
      if (/^\d+$/.test(k)) {
        const alias = `scene_${k}`;
        if (!this.textures.exists(alias)) {
          const img = this.textures.get(k)?.getSourceImage() as HTMLImageElement | undefined;
          if (img) this.textures.addImage(alias, img);
        }
      }
    }
  }



  // ────────────────────────────────────────────────────────────────────────────
  // PRELOAD
  // ────────────────────────────────────────────────────────────────────────────
  preload() {
    this.assets = new AssetManager(this);

    const phChars = this.configData.PhaserConfig?.characters ?? [];
    const phObjs = this.configData.PhaserConfig?.objects ?? [];
    const sheetKeys = new Set<string>();

    // 1) Characters
    for (const ch of phChars) {
      const key = normKey((ch as any)?.idPhaser ?? ch.id ?? ch.name ?? "");
      if (!key) continue;

      const fw = (ch as any)?.sprite?.frameWidth;
      const fh = (ch as any)?.sprite?.frameHeight;

      const rawUrl =
        (ch as any)?.sprite?.url ??
        (ch as any)?.image_url ??
        (ch as any)?.url ??
        this.lookupAssetUrl(key);

      if (Number.isFinite(fw) && Number.isFinite(fh) && rawUrl) {
        sheetKeys.add(key);
        // spritesheet : passe par resolveUrl (mais spritesheet doit aller via loader)
        this.load.spritesheet(key, this.assets.resolveUrl(rawUrl), {
          frameWidth: fw as number,
          frameHeight: fh as number,
        });
      } else if (rawUrl) {
        // image simple : via AssetManager
        this.assets.queue({ kind: "image", key, url: rawUrl });
      }
    }

    // 2) Assets "admin" → on PRECHARGE aussi les scene_* (on est maintenant natif)
    for (const as of this.configData.assets) {
      const k = normKey(as.key);
      if (sheetKeys.has(k)) continue;
      // on ne skip plus les scenes
      this.assets.queue({ kind: "image", key: k, url: as.value });
    }

    if ((this.load as any).setCORS) {
      this.load.setCORS("anonymous");
    }

    // 3) Objets présents au boot
    for (const o of phObjs) {
      const key = normKey((o as any).idPhaser ?? o.id ?? "");
      if (!key || sheetKeys.has(key)) continue;
      if (this.textures.exists(key)) continue;

      const url = (o as any)?.assets?.texture ?? this.lookupAssetUrl(key);
      if (url) this.assets.queue({ kind: "image", key, url });
    }

    // 4) Sons
    for (const s of this.configData.PhaserConfig?.sounds ?? []) {
      this.assets.queue({ kind: "audio", key: s.name, url: (s as any).file_path });
    }

    // 5) Go
    this.assets.preloadAll();
  }


  // ────────────────────────────────────────────────────────────────────────────
  // CREATE
  // ────────────────────────────────────────────────────────────────────────────
  create() {
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.cleanup());

    // Mini-systèmes
    this.colliders = new ColliderRegistry(this);
    this.entities = new EntityManager(this.adminMode, this);
    this.audio = new AudioManager(this.adminMode, this);
    this.ensureSceneAliases();

    // Grid overlay
    this.gridGraphics = this.add.graphics();
    this.gridGraphics.setDepth(10000); // au-dessus de tout
    this.gridGraphics.setVisible(false);
    this.drawGrid();

    // Fond de scène si présent
    if (this.configData.PhaserConfig?.scene) {
      this.updateSceneBackground(this.configData.PhaserConfig.scene);
    }

    // Audio déjà enregistrés
    this.audio.register(this.configData.PhaserConfig?.sounds ?? []);
    this.audio.createAll();

    // Win conditions
    if (this.winConditions.length && this.eventManager) {
      this.winConditionManager = new WinConditionManager(
        this.winConditions,
        this,
        this.eventManager
      );
    }
    this.attachWinListener();

    // Collision system (règles prêtes, rebuild plus tard)
    this.collisions = new CollisionSystem(this, this.colliders, this.entities, {
      events: this.eventManager ?? undefined,
      win: this.winConditionManager ?? undefined,
      debug: false,
    }).configureDefaultRules();

    // 🔒 Boot déterministe: créer les entités APRES chargement des textures runtime
    const initChars = this.configData.PhaserConfig?.characters ?? [];
    const initObjs = this.configData.PhaserConfig?.objects ?? [];
    const initTexts = this.configData.PhaserConfig?.texts ?? [];

    this.ensureCharacterTextures(initChars, () => {
      this.entities.syncCharacters(initChars);

      this.ensureObjectTextures(initObjs, () => {
        this.entities.syncObjects(initObjs);

        // Textes (pas de textures)
        this.entities.syncTexts(initTexts);

        // Collisions une fois tout en place
        this.applyObjectFlagsToBodies();
        this.collisions.rebuild();
      });
    });

    // Monde physique
    this.physics.world.setBoundsCollision(true, true, true, true);
  }


  // ────────────────────────────────────────────────────────────────────────────
  // UPDATE
  // ────────────────────────────────────────────────────────────────────────────
  update() {
    this.forEachCharacter((c) => c.updatePhaserSprite?.());
    this.forEachObject((o) => o.updatePhaserSprite?.());
    this.winConditionManager?.update();
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Public API
  // ────────────────────────────────────────────────────────────────────────────
  public setGridEnabled(enabled: boolean, cellSize: number = 50) {
    this.gridEnabled = !!enabled;
    this.gridCellSize = cellSize || 50;
    this.gridGraphics?.setVisible(this.gridEnabled);
    if (this.gridEnabled) this.drawGrid();
  }
  public reset() {
    this._hasWon = false;
    this.nextPickOrder = 1;
    this.setGravity(false);
    this.entities.destroyAll();
    this.collisions.rebuild();
    this.winConditionManager?.reset();
  }

  public emit(eventName: string, ...args: any[]) {
    this.eventManager?.emit(eventName, ...args);
  }

  public setGravity(active: boolean, y: number = DEFAULT_GRAVITY_Y) {
    const w = this.physics.world;
    w.gravity.set(0, active ? y : 0);
    if (!active) {
      this.children.list.forEach((go) => {
        const body = (go as any).body as Phaser.Physics.Arcade.Body | undefined;
        if (body) body.setVelocityY(0);
      });
    }
    // ⬇️ très important
    this.applyObjectFlagsToBodies();
  }

  public triggerWin(): void {
    if (this._hasWon) return;
    this._hasWon = true;

    // 1) animation de victoire sur tous les persos
    try { this.forEachCharacter((c) => c.playWinAnimation?.()); } catch { }

    // 2) callback vers React (fourni par le constructeur)
    try { this.onWin?.(); } catch { }
  }


  public handleCharacters(chars: CharacterConfig[]) {
    // Always keep latest characters in config so create() can consume them
    this.configData.PhaserConfig.characters = chars;

    // If the scene isn't ready yet, let create() handle initial sync
    if (!this.entities || !this.collisions) return;

    this.ensureCharacterTextures(chars, () => {
      this.entities.syncCharacters(chars);
      this.collisions.rebuild();
    });
  }

  public handleObjects(objs: ObjectConfig[]) {
    // Keep config up to date for initial boot
    this.configData.PhaserConfig.objects = objs;

    if (!this.entities || !this.collisions) return;

    this.ensureObjectTextures(objs, () => {
      this.entities.syncObjects(objs);
      this.applyObjectFlagsToBodies();
      this.collisions.rebuild();
    });
  }

  public handleTexts(texts: TextObjectConfig[]) {
    // Persist texts in config for boot-time use
    this.configData.PhaserConfig.texts = texts;

    if (!this.entities) return;

    this.entities.syncTexts(texts);
  }

  public setWinConditions(conds: WinCondition[]) {
    this.winConditions = conds ?? [];

    // nettoyer ancien manager + ancien listener
    try { this.winConditionManager?.dispose?.(); } catch { }
    this.winConditionManager = undefined;

    try { this.winEventDisposer?.(); } catch { }
    this.winEventDisposer = undefined;

    // recréer proprement
    if (this.eventManager && this.winConditions.length) {
      this.winConditionManager = new WinConditionManager(this.winConditions, this, this.eventManager);
    }

    // rebrancher l'écoute "win" vers triggerWin()
    this.attachWinListener();
  }

  // ----- Flags 
  private applyFlagsAndBody(obj: any) {
    if (!obj?.sprite) return;
    const s = obj.sprite as Phaser.GameObjects.GameObject & { body?: Phaser.Physics.Arcade.Body };
    const body = s.body;

    const flags = {
      immovable: !!obj.immovable,
      pushable: !!obj.pushable,
      pickable: !!obj.pickable,
    };

    s.setData("immovable", flags.immovable);
    s.setData("pushable", flags.pushable);
    s.setData("pickable", flags.pickable);
    s.setData("flags", flags);

    if (body) {
      // 🔒 Cas décor (même logique que dans applyObjectFlagsToBodies)
      if (obj.decor) {
        body.setDamping(false);
        body.setDrag(PUSHABLE.DRAG, 0);
        body.setVelocity(0, 0);
        body.moves = false;
        body.setImmovable(true);
        (body as any).immovable = true; // double assign pour éviter un flip ailleurs
        body.allowGravity = false;
        return; // ne rien toucher d’autre après
      }

      // ... puis tes branches pick/imm/push/neutre actuelles ...
      if (flags.pickable) {
        body.setDamping(false);
        body.setDrag(PUSHABLE.DRAG, 0);
        body.moves = true;
        body.setImmovable(false);
      } else if (flags.immovable) {
        body.setDamping(false);
        body.setDrag(PUSHABLE.DRAG, 0);
        body.moves = true;
        body.setImmovable(true);
      } else if (flags.pushable) {
        body.setDamping(false);
        body.setDrag(PUSHABLE.DRAG, PUSHABLE.DRAG);
        body.moves = true;
        body.setImmovable(false);
      } else {
        body.setDamping(false);
        body.setDrag(PUSHABLE.DRAG, 0);
        body.moves = true;
        body.setImmovable(false);
      }

      // Une seule décision “gravité”
      body.allowGravity = this.computeAllowGravity(obj);
    } else {
      s.setData("hasBody", false);
    }
  }


  // ──────────────────────────────────────────────────────────────
  // MÉTHODES PUBLIQUES MISES À JOUR
  // ──────────────────────────────────────────────────────────────
  public setObjectPickable(objectId: string, pickOrder = 0) {
    const obj = this.entities.getObject(objectId);
    if (!obj || !obj.sprite) return;

    obj.pickable = true;
    obj.pickOrder = pickOrder;

    // si l’objet devient pickable → il ne doit plus être pushable ou immovable
    obj.immovable = false;
    obj.pushable = false;

    this.applyFlagsAndBody(obj);
    this.collisions.rebuild();
  }

  public clearObjectPickable(objectId: string) {
    const obj = this.entities.getObject(objectId);
    if (!obj || !obj.sprite) return;

    obj.pickable = false;

    // on ne modifie pas immovable/pushable, on laisse leurs états tels quels
    this.applyFlagsAndBody(obj);
    this.collisions.rebuild();
  }

  public setObjectImmovable(objectId: string) {
    const obj = this.entities.getObject(objectId);
    if (!obj || !obj.sprite) return;

    obj.immovable = true;
    // si immovable passe à true, pushable n’a plus de sens
    obj.pushable = false;

    this.applyFlagsAndBody(obj);
    this.collisions.rebuild();
  }

  public clearObjectImmovable(objectId: string) {
    const obj = this.entities.getObject(objectId);
    if (!obj || !obj.sprite) return;

    obj.immovable = false;
    // on ne force pas pushable ici ; on laisse son état tel quel
    this.applyFlagsAndBody(obj);
    this.collisions.rebuild();
  }

  public setObjectPushable(objectId: string) {
    const obj = this.entities.getObject(objectId);
    if (!obj || !obj.sprite) return;

    obj.pushable = true;
    // pushable et immovable sont exclusifs
    obj.immovable = false;
    // si l’objet était pickable, on le désactive (push != pick)
    if (obj.pickable) obj.pickable = false;

    this.applyFlagsAndBody(obj);
    this.collisions.rebuild();
  }

  public clearObjectPushable(objectId: string) {
    const obj = this.entities.getObject(objectId);
    if (!obj || !obj.sprite) return;

    obj.pushable = false;
    this.applyFlagsAndBody(obj);
    this.collisions.rebuild();
  }

  // dans class PhaserScene
  private computeAllowGravity(o: any): boolean {
    const gy = this.physics.world?.gravity?.y ?? 0;
    return gy !== 0 && !o?.decor;
  }

  public setObjectDecor(objectId: string) {
    const obj = this.entities.getObject(objectId);
    if (!obj || !obj.sprite) return;

    obj.decor = true;
    // un décor est immobile par nature
    obj.immovable = true;
    obj.pushable = false;
    // décor ne doit pas être pickable
    obj.pickable = false;

    this.applyFlagsAndBody(obj);
    this.collisions.rebuild();
  }

  public clearObjectDecor(objectId: string) {
    const obj = this.entities.getObject(objectId);
    if (!obj || !obj.sprite) return;

    obj.decor = false;
    // on laisse immovable/pushable/pickable tels qu’ils étaient
    this.applyFlagsAndBody(obj);
    this.collisions.rebuild();
  }

  // PhaserScene.ts



  // ----- Background (auto-load if missing)
  // ----- Background (auto-load if missing)
  public updateSceneBackground(scene: SceneConfig) {
    if (!scene) return;

    // redimensionner la scène si besoin
    if (this.scale) this.scale.resize(scene.width, scene.height);

    // id requis
    const rawId = String(scene.id ?? "").trim();
    if (!rawId) {
      console.warn("[BG] scene.id vide — on ignore");
      return;
    }

    const wanted = rawId;
    const keyNS = wanted.startsWith("scene_") ? normKey(wanted) : normKey(`scene_${wanted}`);
    const exists = (k: string) => this.textures.exists(k);

    // 1) Cas idéal : déjà préchargé → on applique directement dans Phaser
    if (exists(keyNS)) {
      this.clearDomBackground();
      let bg = this.children.getByName("bg_image") as Phaser.GameObjects.Image | undefined;
      if (bg) bg.setTexture(keyNS);
      else bg = this.add.image(0, 0, keyNS).setName("bg_image");

      const img = this.textures.get(keyNS)?.getSourceImage() as HTMLImageElement | undefined;
      if (!img) return;

      const { width: sceneW, height: sceneH } = scene;
      const scaleX = sceneW / img.width;
      const scaleY = sceneH / img.height;
      const finalScale = Math.max(scaleX, scaleY);

      bg.setOrigin(0.5, 0.5);
      bg.setPosition(sceneW / 2, sceneH / 2);
      bg.setScale(finalScale);
      bg.setDepth(-1000);
      return;
    }

    // 2) Pas en cache → on tente un lazy-load Phaser, puis fallback CSS si nécessaire
    const urlFromAssets =
      this.lookupAssetUrl(keyNS) || this.lookupAssetUrl(wanted) || undefined;

    if (urlFromAssets) {
      const finalUrl = (this.assets as any)?.resolveUrl?.(urlFromAssets) ?? urlFromAssets;

      const onFile = (fileKey: string) => {
        // ne réagit qu'à LA bonne ressource
        if (normKey(fileKey) !== keyNS) return;

        this.load.off(Phaser.Loader.Events.FILE_COMPLETE, onFile);

        if (this.textures.exists(keyNS)) {
          // On applique comme dans le cas idéal
          this.clearDomBackground();
          let bg = this.children.getByName("bg_image") as Phaser.GameObjects.Image | undefined;
          if (bg) bg.setTexture(keyNS);
          else bg = this.add.image(0, 0, keyNS).setName("bg_image");

          const img = this.textures.get(keyNS)?.getSourceImage() as HTMLImageElement | undefined;
          if (!img) return;

          const { width: sceneW, height: sceneH } = scene;
          const scaleX = sceneW / img.width;
          const scaleY = sceneH / img.height;
          const finalScale = Math.max(scaleX, scaleY);

          bg.setOrigin(0.5, 0.5);
          bg.setPosition(sceneW / 2, sceneH / 2);
          bg.setScale(finalScale);
          bg.setDepth(-1000);
        } else {
          // très rare : si la texture n’est toujours pas en cache → fallback CSS
          this.applyDomBackground(finalUrl, scene.width, scene.height);
        }
      };

      this.load.on(Phaser.Loader.Events.FILE_COMPLETE, onFile);
      this.load.image(keyNS, finalUrl);
      if (!this.load.isLoading()) this.load.start();
      return;
    }

    // 3) Rien dans la DB d'assets → fallback CSS neutre
    console.warn("[BG] URL introuvable pour", keyNS, "— fallback CSS neutre.");
    this.applyDomBackground("", scene.width, scene.height);
  }




  // ────────────────────────────────────────────────────────────────────────────
  // Getters
  // ────────────────────────────────────────────────────────────────────────────
  public getEventManager() { return this.eventManager; }
  public getCharacterById(id: string) { return this.entities.getCharacter(id); }
  public getObjectById(id: string) { return this.entities.getObject(id); }
  public getTextObjectById(id: string) { return this.entities.getText(id); }

  public getCharacterFirst(): Character | undefined {
    const it = this["entities"]["characters"]?.values?.();
    return it ? (it.next().value as Character | undefined) : undefined;
  }

  public getAllCharacterIds(): string[] {
    return Array.from(this.entities["characters"].keys());
  }

  public getAllObjectIds(): string[] {
    return Array.from(this.entities["objects"].keys());
  }

  public getAllTextIds(): string[] {
    return Array.from(this.entities["texts"].keys());
  }

  public getNextPickOrder() { return this.nextPickOrder; }
  public incrementPickOrder() { this.nextPickOrder++; }

  public isObjectPicked(objectId: string): boolean {
    const obj = this.getObjectById(objectId);
    return !!obj && obj.pickable && obj.currentState === "carried";
  }
  public areAllPickablesPicked(): boolean {
    const map = (this as any).entities["objects"] as Map<string, GameObject>;
    if (!map) return true;
    for (const o of map.values()) if (o.pickable && o.currentState !== "carried") return false;
    return true;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Internal helpers
  // ────────────────────────────────────────────────────────────────────────────
  private attachWinListener() {
    if (!this.eventManager) return;

    const handler = () => this.triggerWin();
    this.eventManager.on("win", handler);

    // Disposer (compatible avec EventManager sans .off)
    this.winEventDisposer = () => {
      const em: any = this.eventManager;
      if (!em) return;
      if (em.off) em.off("win", handler);
      else {
        const arr = em?.genericListeners?.get?.("win");
        if (arr && Array.isArray(arr)) {
          const i = arr.indexOf(handler);
          if (i !== -1) arr.splice(i, 1);
        }
      }
    };
  }

  private forEachCharacter(fn: (c: Character) => void) {
    const map = (this as any).entities["characters"] as Map<string, Character>;
    if (!map) return;
    for (const c of map.values()) fn(c);
  }
  private forEachObject(fn: (o: GameObject) => void) {
    const map = (this as any).entities["objects"] as Map<string, GameObject>;
    if (!map) return;
    for (const o of map.values()) fn(o);
  }

  // PhaserScene.ts
  private applyObjectFlagsToBodies() {
    this.forEachObject((o) => {
      if (!o.sprite) return;

      // 1) vérité métier actuelle
      const imm = !!o.immovable;
      const push = !!o.pushable;
      const pick = !!o.pickable;

      // 2) Corps Arcade 
      const body = o.sprite.body as Phaser.Physics.Arcade.Body | undefined;


      // 3) Data plates + objet flags unifié (utilisé par CollisionSystem)
      o.sprite.setData("immovable", imm);
      o.sprite.setData("pushable", push);
      o.sprite.setData("pickable", pick);
      o.sprite.setData("flags", { immovable: imm, pushable: push, pickable: pick });


      if (body) {
        body.setBounce(0, 0);
        body.setMaxVelocity(PUSHABLE.MAX_VELOCITY, PUSHABLE.MAX_VELOCITY);

        // 🔒 Cas décor: jamais de gravité, figé
        if (o.decor) {
          body.setDamping(false);
          body.setDrag(PUSHABLE.DRAG, 0);
          body.setVelocity(0, 0);
          body.moves = false;
          body.setImmovable(true);
          body.immovable = true;          // double-assign pour éviter un flip ailleurs
          body.allowGravity = false;      // ✅ source unique mais on force ici pour éviter tout bug d'ordre
          return;                         // ⬅️ on ne laisse personne le re-modifier après
        }

        if (push) {
          body.setDamping(false);
          body.setDrag(PUSHABLE.DRAG, PUSHABLE.DRAG);
          body.moves = true;
          body.setImmovable(false);
        } else if (pick) {
          body.setDamping(false);
          body.setDrag(PUSHABLE.DRAG, 0);
          body.moves = true;           // ✅ pickable tombe
          body.setImmovable(false);    // ✅ pas immovable.
          body.immovable = false;
          // ne PAS forcer setVelocity(0,0) ici
        } else if (imm) {
          body.setDamping(false);
          body.setDrag(PUSHABLE.DRAG, 0);
          body.moves = true;           // ✅ immovable tombe (mais ne bouge pas aux collisions)
          body.setImmovable(true);
        } else {
          body.setDamping(false);
          body.setDrag(PUSHABLE.DRAG, 0);
          body.moves = true;           // ✅ neutre tombe
          body.setImmovable(false);
        }

        // Gravité: une seule source de vérité
        body.allowGravity = this.computeAllowGravity(o); // => gy !== 0 && !o.decor
      }

    });
  }


  private getCharacterBySprite(sprite: Phaser.GameObjects.GameObject | any): Character | undefined {
    let found: Character | undefined;
    this.forEachCharacter((c) => { if (c.sprite === sprite) found = c; });
    return found;
  }
  private getObjectBySprite(sprite: Phaser.GameObjects.GameObject | any): GameObject | undefined {
    let found: GameObject | undefined;
    this.forEachObject((o) => { if (o.sprite === sprite) found = o; });
    return found;
  }

  // ───────────── Runtime hot-loaders (with assets fallback) ─────────────
  private ensureObjectTextures(objs: ObjectConfig[], done: () => void) {
    const items: Array<{ key: string; url: string }> = [];

    for (const o of objs) {
      const key = normKey((o as any).idPhaser ?? o.id ?? "");
      if (!key || this.textures.exists(key)) continue;

      const rawUrl = (o as any)?.assets?.texture ?? this.lookupAssetUrl(key);
      if (!rawUrl) continue;
      items.push({ key, url: rawUrl });
    }

    if (!items.length) { done(); return; }

    const onComplete = () => {
      this.load.off(Phaser.Loader.Events.COMPLETE, onComplete);
      done();
    };
    this.load.on(Phaser.Loader.Events.COMPLETE, onComplete);

    for (const it of items) {
      this.load.image(it.key, this.assets.resolveUrl(it.url));
    }
    if (!this.load.isLoading()) this.load.start();
  }


  // PhaserScene.ts
  private ensureCharacterTextures(chars: CharacterConfig[], done: () => void) {
    type Sheet = { key: string; url: string; fw: number; fh: number };
    type Img = { key: string; url: string };
    const sheets: Sheet[] = [];
    const images: Img[] = [];

    for (const ch of chars) {
      const key = normKey((ch as any).idPhaser ?? ch.id ?? ch.name ?? "");
      if (!key || this.textures.exists(key)) continue;

      const fw = (ch as any)?.sprite?.frameWidth;
      const fh = (ch as any)?.sprite?.frameHeight;
      const rawUrl =
        (ch as any)?.sprite?.url ??
        (ch as any)?.image_url ??
        (ch as any)?.url ??
        this.lookupAssetUrl(key);
      if (!rawUrl) continue;

      if (Number.isFinite(fw) && Number.isFinite(fh)) {
        sheets.push({ key, url: rawUrl, fw: Number(fw), fh: Number(fh) });
      } else {
        images.push({ key, url: rawUrl });
      }
    }

    if (!sheets.length && !images.length) { done(); return; }

    const onComplete = () => {
      this.load.off(Phaser.Loader.Events.COMPLETE, onComplete);
      done();
    };
    this.load.on(Phaser.Loader.Events.COMPLETE, onComplete);

    for (const s of sheets) {
      this.load.spritesheet(s.key, this.assets.resolveUrl(s.url), {
        frameWidth: s.fw, frameHeight: s.fh
      });
    }
    for (const i of images) {
      this.load.image(i.key, this.assets.resolveUrl(i.url));
    }
    if (!this.load.isLoading()) this.load.start();
  }


  // ────────────────────────────────────────────────────────────────────────────
  // Audio helpers (public)
  // ────────────────────────────────────────────────────────────────────────────
  public getAudioManager() {
    return this.audio;
  }

  /** Retourne l'instance Sound (notre classe) si elle existe déjà. */
  public getSoundByName(name: string) {
    return this.audio?.getByName?.(name);
  }

  /**
   * Charge le son s'il n'est pas en cache, puis crée l'instance Phaser,
   * puis appelle onReady(). Utilisé par playSound() en lazy-load.
   */
  public ensureSound(name: string, onReady?: () => void) {
    const list = this.configData?.PhaserConfig?.sounds ?? [];
    const cfg = list.find(s => String(s?.name) === String(name));
    if (!cfg) {
      console.warn(`[ensureSound] Son "${name}" absent de PhaserConfig.sounds.`);
      return;
    }

    const key = cfg.name;                // clé Phaser = name
    const url = cfg.file_path; // URL finale (CDN / API)
    // Charge dans Phaser s'il le faut, puis crée l'instance
    this.assets?.ensureAudio(key, url, () => {
      // Demande à l'audio manager de créer/attacher le sound à la scène
      this.audio?.createOne?.(key);
      onReady?.();
    });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Cleanup
  // ────────────────────────────────────────────────────────────────────────────
  public cleanup() {
    try { this.winEventDisposer?.(); } catch { }
    this.winEventDisposer = undefined;
    try { this.clearDomBackground(); } catch { }
    try { this.winConditionManager?.dispose?.(); } catch { }
    try { this.audio?.destroy?.(); } catch { }
    try { this.colliders?.destroyAll?.(); } catch { }
    try { this.entities?.destroyAll?.(); } catch { }
  }

  private drawGrid() {
    if (!this.gridGraphics) return;

    const w = this.scale?.width ?? this.sys.game.config.width as number;
    const h = this.scale?.height ?? this.sys.game.config.height as number;
    const s = this.gridCellSize;

    this.gridGraphics.clear();
    this.gridGraphics.lineStyle(1, 0x000000, 0.15);

    for (let x = 0; x <= w; x += s) {
      this.gridGraphics.beginPath();
      this.gridGraphics.moveTo(x, 0);
      this.gridGraphics.lineTo(x, h);
      this.gridGraphics.strokePath();
    }

    for (let y = 0; y <= h; y += s) {
      this.gridGraphics.beginPath();
      this.gridGraphics.moveTo(0, y);
      this.gridGraphics.lineTo(w, y);
      this.gridGraphics.strokePath();
    }
  }

}
